// --------------------------------------------------------------
// Created On: 2023-10-2
// Author: Zachary Thomas
//
// Last Modified: 2025-04-11
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------
import React, { useState } from "react";
import PropTypes from "prop-types";
import { ENABLE_FIRMWARE_UPDATES } from "src/constants/miscellaneous";
import IconTooltip from "../../../../components/IconTooltip/IconTooltip";
import isoIsValid from "../../../../utilities/time/isoIsValid";
import formatDateLocal from "../../../../utilities/time/formatDateLocal";
import styles from "./DeviceStatusValue.module.scss";
import userHasPermission from "src/utilities/userHasPermission";
import { APPLY_FIRMWARE_PERMISSION } from "src/constants/permissions";
import ConfirmModal from "src/components/ConfirmModal/ConfirmModal";

// A row that shows the value and description for a specific type of device status.
export default function DeviceStatusValue(props: Props): Component {
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);

  return (
    <tr className={styles.body}>
      <td>
        <label className={styles.label}>{props.name}</label>
      </td>
      <td>
        <div className="row">
          <div className="col">
            {isoIsValid(props.value.split(".")[0]) ? formatDateLocal(props.value) : props.value}
            {props.name === "RSSI" && <span> dBm</span>}
          </div>
          {props.name === "Firmware Version" &&
            userHasPermission([[APPLY_FIRMWARE_PERMISSION]]) &&
            ENABLE_FIRMWARE_UPDATES && (
              <div className="col">
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowUpdateModal(true)}>
                  Update Firmware
                </button>
              </div>
            )}
        </div>
      </td>
      <td>
        <IconTooltip
          id={`status-row-${props.name}`}
          icon="info-circle"
          message={props.description}
          color="var(--info-tooltip)"
        />
      </td>

      <ConfirmModal
        showModal={showUpdateModal}
        title="Update Firmware?"
        content={"Would you like to update the device's current firmware?"}
        yesText="Update Firmware"
        noText="Cancel"
        danger={true}
        onClose={() => setShowUpdateModal(false)}
        onYes={() => {
          props.onUpdateFirmware();
          setShowUpdateModal(false);
        }}
        onNo={() => setShowUpdateModal(false)}
      />
    </tr>
  );
}

DeviceStatusValue.propTypes = {
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onUpdateFirmware: PropTypes.func.isRequired,
};

interface Props {
  name: string;
  description: string;
  value: string;
  onUpdateFirmware: () => void;
}
